import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import {
  auth,
  db
} from '../firebase/config';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../firebase/collections';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser]   = useState(null);
  const [userRole, setUserRole]         = useState(null);
  const [userSchool, setUserSchool]     = useState(null);
  const [userService, setUserService]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const mounted                  = useRef(true);
  const authOperationInProgress  = useRef(false);
  const authStateInitialized     = useRef(false);

  const getUserData = useCallback(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));

      if (!userDoc.exists()) return null;

      const userData = userDoc.data();
      let enrichedData = { ...userData, id: uid };

      // Récupérer l'école pour les rôles non-service
      if (userData.role !== 'service' && userData.role !== 'technical' && userData.schoolId) {
        const schoolDoc = await getDoc(doc(db, COLLECTIONS.SCHOOLS, userData.schoolId));
        if (schoolDoc.exists()) {
          enrichedData.school = { id: schoolDoc.id, ...schoolDoc.data() };
        }
      }

      // Récupérer le service si non déjà présent dans le document
      if ((userData.role === 'service' || userData.role === 'technical') && !userData.service) {
        if (!userData.serviceId) {
          console.error('getUserData: serviceId manquant pour l\'utilisateur', uid);
          return enrichedData;
        }

        const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, userData.serviceId));
        if (serviceDoc.exists()) {
          enrichedData.service = { id: serviceDoc.id, ...serviceDoc.data() };
        } else {
          console.warn('getUserData: service introuvable, utilisation d\'un service par défaut');
          enrichedData.service = { id: userData.serviceId, name: 'Service Technique' };
        }
      }

      return enrichedData;
    } catch (error) {
      console.error('getUserData: Erreur lors de la récupération des données utilisateur:', error);
      return null;
    }
  }, []);

  const updateUserState = useCallback(async (user) => {
    try {
      if (!user) {
        setCurrentUser(null);
        setUserRole(null);
        setUserSchool(null);
        setUserService(null);
        return;
      }

      const userData = await getUserData(user.uid);

      if (userData) {
        setCurrentUser(user);
        setUserRole(userData.role);

        if (userData.role === 'service' || userData.role === 'technical') {
          setUserSchool(userData.school || null);

          if (userData.service) {
            setUserService(userData.service);
          } else {
            console.warn('updateUserState: service manquant pour l\'utilisateur', user.uid);
            setUserService({ id: userData.serviceId || user.uid, name: 'Service Technique' });
          }
        } else {
          setUserSchool(userData.school || null);
          setUserService(null);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setUserSchool(null);
        setUserService(null);
        await signOut(auth);
      }
    } catch (error) {
      console.error('updateUserState: Erreur lors de la mise à jour:', error);
      setError(error.message);
    }
  }, [getUserData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        await updateUserState(user);
      } finally {
        if (!authStateInitialized.current) {
          authStateInitialized.current = true;
          setLoading(false);
        }
      }
    });

    return () => {
      mounted.current = false;
      if (unsubscribe) unsubscribe();
    };
  }, [updateUserState]);

  const login = useCallback(async (email, password) => {
    if (authOperationInProgress.current) return;
    authOperationInProgress.current = true;

    try {
      setError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      let message = 'Erreur lors de la connexion';

      switch (error.code) {
        case 'auth/user-not-found':
          message = 'Aucun compte ne correspond à cet email';
          break;
        case 'auth/wrong-password':
          message = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          message = 'Adresse email invalide';
          break;
        case 'auth/user-disabled':
          message = 'Ce compte a été désactivé';
          break;
        default:
          message = error.message;
      }

      setError(message);
      throw error;
    } finally {
      authOperationInProgress.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    if (authOperationInProgress.current) return;
    authOperationInProgress.current = true;

    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserRole(null);
      setUserSchool(null);
      setUserService(null);
    } catch (error) {
      console.error('logout: Erreur lors de la déconnexion:', error);
      throw error;
    } finally {
      authOperationInProgress.current = false;
    }
  }, []);

  const createService = useCallback(async (serviceData, password) => {
    if (authOperationInProgress.current) return;
    authOperationInProgress.current = true;

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        serviceData.email,
        password
      );
      return userCredential.user;
    } catch (error) {
      console.error('createService: Erreur lors de la création:', error);
      throw error;
    } finally {
      authOperationInProgress.current = false;
    }
  }, []);

  const value = {
    currentUser,
    userRole,
    userSchool,
    userService,
    loading,
    error,
    login,
    logout,
    getUserData,
    createService,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
